import React, { memo, useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Text, Button, SkeletonGroup, SkeletonLine, Divider } from '@wix/design-system';
import { navigateTo, PageComponentId } from '@wix/business-manager-api';
import { getModel, getModelData } from '@wix/ambassador-analytics-ng-reports-model-v1-model/http';
import { addDays, isEqual } from 'date-fns';
import { backfillWithTime, getPeriodString } from './utils';
import { EXCLUDED_FILTER, accountLanguage, wwwWix, manageWix, bo } from './constants';
export const ReportButton = /*#__PURE__*/memo(_ref => {
  let {
    data,
    context: {
      httpClient
    }
  } = _ref;
  const {
    reportId,
    query
  } = data;
  const {
    id,
    ...rest
  } = query;
  const {
    timeframe,
    fields
  } = rest;
  const [isOneFieldPreview, setIsOneFieldPreview] = useState(undefined);
  const [modelSlug, setModelSlug] = useState('');
  const [value, setValue] = useState('');
  const [items, setItems] = useState([]);
  const [translations, setTranslations] = useState({});
  const isPreviewVisible = useMemo(() => fields && fields.length > 0 && fields.length <= 2, [fields]);
  const getDomain = useCallback(() => {
    if (location.href.startsWith(wwwWix)) {
      return wwwWix;
    }
    if (location.href.startsWith(manageWix)) {
      return manageWix;
    }
    return bo;
  }, []);
  const loadTranslations = useCallback(async () => {
    var _res$data;
    const res = await (httpClient == null ? void 0 : httpClient.get(`${getDomain()}/_serverless/analytics-ng-i18n/messages/en`));

    // @ts-expect-error
    setTranslations((res == null || (_res$data = res.data) == null ? void 0 : _res$data.values) ?? {});
  }, [httpClient, getDomain]);
  const loadPreviewData = useCallback(async () => {
    var _dataResponse$data, _modelResponse$data;
    const [dataResponse, modelResponse] = await Promise.all([isPreviewVisible && (httpClient == null ? void 0 : httpClient.request(getModelData({
      ...query,
      timeframe: {
        startDate: timeframe == null ? void 0 : timeframe.startDate,
        endDate: timeframe == null ? void 0 : timeframe.endDate
      },
      modelId: id,
      applyFormatting: true
    }))), httpClient == null ? void 0 : httpClient.request(getModel({
      modelId: id
    }))]);
    const previewData =
    // @ts-expect-error
    (dataResponse == null || (_dataResponse$data = dataResponse.data) == null ? void 0 : _dataResponse$data.data) ?? [];
    const previewDataFirstItem = previewData[0] ?? {};
    const isOneFieldPreviewValue = previewData.length === 1 || Object.keys(previewDataFirstItem).length === 1;
    setModelSlug((modelResponse == null || (_modelResponse$data = modelResponse.data) == null || (_modelResponse$data = _modelResponse$data.model) == null ? void 0 : _modelResponse$data.slug) ?? '');
    setIsOneFieldPreview(isOneFieldPreviewValue);
    if (isOneFieldPreviewValue === true) {
      setValue(Object.values(previewDataFirstItem)[0]);
    } else if (isOneFieldPreviewValue === false) {
      setItems(previewData.slice(0, 3).map(el => ({
        name: Object.values(el)[0],
        value: Object.values(el)[1]
      })));
    }
  }, [isPreviewVisible, httpClient, id, query, timeframe]);
  useEffect(() => {
    loadTranslations();
    loadPreviewData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const params = useMemo(() => rest ? `?q=${btoa(JSON.stringify(rest))}` : '', [rest]);
  const onClick = useCallback(() => navigateTo({
    pageComponentId: PageComponentId.AnalyticsNgBm,
    contextData: {
      appState: `${reportId}?${params}`
    }
  }), [reportId, params]);
  const t = useCallback(key => translations[key], [translations]);
  const pickTranslation = useCallback(options => t(options[0]) ?? t(options[1]) ?? options[1], [t]);
  const hasTranslation = useCallback(options => t(options[0]) !== undefined || t(options[1]) !== undefined, [t]);
  const getReportNativeKeyOptions = useCallback(key => [`report-native.${modelSlug}.${key}`, `report-native.${key}`], [modelSlug]);
  const pickReportNativeTranslation = useCallback(key => pickTranslation(getReportNativeKeyOptions(key)), [getReportNativeKeyOptions, pickTranslation]);
  const hasReportNativeTranslation = useCallback(key => hasTranslation(getReportNativeKeyOptions(key)), [getReportNativeKeyOptions, hasTranslation]);
  const periodString = useMemo(() => {
    if (timeframe) {
      const startDate = new Date(backfillWithTime(timeframe.startDate));
      const endDate = addDays(new Date(backfillWithTime(timeframe.endDate)), -1);
      return getPeriodString({
        startDate,
        endDate: isEqual(startDate, endDate) ? undefined : endDate
      }, accountLanguage,
      // to do: replace with translation keys
      'Today', 'Yesterday');
    }
    return '';
  }, [timeframe]);
  const fieldList = useMemo(() => [...new Set((fields == null ? void 0 : fields.map(field => pickReportNativeTranslation(`column.${field}`))) ?? [])].join(', '), [fields, pickReportNativeTranslation]);
  const filterList = useMemo(() => {
    var _filters$filter;
    const {
      filters
    } = query;
    return [...new Set((filters == null || (_filters$filter = filters.filter(_ref2 => {
      let {
        field
      } = _ref2;
      return !(field != null && field.endsWith(EXCLUDED_FILTER)) && hasReportNativeTranslation(`filter.${field}`);
    })) == null ? void 0 : _filters$filter.map(_ref3 => {
      let {
        field
      } = _ref3;
      return pickReportNativeTranslation(`filter.${field}`);
    })) ?? [])].join(', ');
  }, [query, hasReportNativeTranslation, pickReportNativeTranslation]);
  const reportContent = useMemo(() => /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP1"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "normal",
    secondary: true
  }, "Report Settings"), /*#__PURE__*/React.createElement(Box, {
    gap: "SP1"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "normal",
    secondary: true
  }, "Time:"), /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    secondary: true
  }, periodString)), fieldList.length > 0 && /*#__PURE__*/React.createElement(Box, {
    display: "inline",
    lineHeight: "1"
  }, /*#__PURE__*/React.createElement(Box, {
    display: "inline"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "normal",
    secondary: true
  }, "Columns:")), /*#__PURE__*/React.createElement(Box, {
    display: "inline",
    margin: "SP1"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    secondary: true
  }, fieldList))), filterList.length > 0 && /*#__PURE__*/React.createElement(Box, {
    display: "inline",
    lineHeight: "1"
  }, /*#__PURE__*/React.createElement(Box, {
    display: "inline"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "normal",
    secondary: true
  }, "Filtered by:")), /*#__PURE__*/React.createElement(Box, {
    display: "inline",
    margin: "SP1"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    secondary: true
  }, filterList)))), [fieldList, filterList, periodString]);
  const previewLoader = useMemo(() => /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP1"
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Box, {
    width: "50%"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    secondary: true,
    ellipsis: true
  }, pickReportNativeTranslation(`column.${fields == null ? void 0 : fields[0]}`))), (fields == null ? void 0 : fields[1]) && /*#__PURE__*/React.createElement(Box, {
    width: "50%",
    align: "right"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    secondary: true,
    ellipsis: true
  }, pickReportNativeTranslation(`column.${fields == null ? void 0 : fields[1]}`)))), /*#__PURE__*/React.createElement(SkeletonGroup, {
    skin: "light"
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "9px"
  }, new Array(3).fill(null).map((_, i) => /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    key: `report-button-skeleton-${i}`
  }, /*#__PURE__*/React.createElement(Box, {
    align: "space-between"
  }, /*#__PURE__*/React.createElement(SkeletonLine, {
    width: "59px"
  }), /*#__PURE__*/React.createElement(SkeletonLine, {
    width: "31px"
  }))))))), [fields, pickReportNativeTranslation]);
  const emptyAvgSessionDurationValueText = useMemo(() => /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    secondary: true,
    ellipsis: true
  }, "Not enough data to calculate session duration."), []);
  const emptyValueText = useMemo(() => /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    secondary: true,
    ellipsis: true
  }, "This type of activity didn't occur during this period."), []);
  const oneFieldPreviewComponent = useMemo(() => {
    const translatedColumn = pickReportNativeTranslation(`column.${fields == null ? void 0 : fields[0]}`).toString().toLowerCase();
    const isEmptyValue = [null, undefined, 'null', 'undefined'].includes(value);
    if (translatedColumn === 'avg. session duration' && isEmptyValue) {
      return emptyAvgSessionDurationValueText;
    }
    if (isEmptyValue) {
      return emptyValueText;
    }
    return /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Text, {
      size: "small",
      weight: "bold"
    }, `${value} ${translatedColumn}`));
  }, [emptyAvgSessionDurationValueText, emptyValueText, fields, pickReportNativeTranslation, value]);
  const twoFieldsPreviewComponent = useMemo(() => items.length === 0 ? emptyValueText : /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP1"
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Box, {
    width: "50%"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    secondary: true,
    ellipsis: true
  }, pickReportNativeTranslation(`column.${fields == null ? void 0 : fields[0]}`))), /*#__PURE__*/React.createElement(Box, {
    width: "50%",
    align: "right"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    secondary: true,
    ellipsis: true
  }, pickReportNativeTranslation(`column.${fields == null ? void 0 : fields[1]}`)))), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP1"
  }, items.map((item, i) => /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    key: `report-button-preview-item-${i}`
  }, /*#__PURE__*/React.createElement(Box, {
    align: "space-between"
  }, /*#__PURE__*/React.createElement(Box, {
    width: "60%"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "normal",
    ellipsis: true
  }, item.name)), /*#__PURE__*/React.createElement(Box, {
    width: "40%",
    align: "right"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "bold",
    ellipsis: true
  }, item.value))))))), [fields, items, pickReportNativeTranslation]);
  const previewContent = useMemo(() => {
    if (isOneFieldPreview === undefined) {
      return previewLoader;
    }
    return isOneFieldPreview ? oneFieldPreviewComponent : twoFieldsPreviewComponent;
  }, [isOneFieldPreview, oneFieldPreviewComponent, previewLoader, twoFieldsPreviewComponent]);
  return /*#__PURE__*/React.createElement(Box, {
    width: "100%",
    direction: "vertical",
    gap: "SP2"
  }, /*#__PURE__*/React.createElement(Box, {
    padding: "SP2 SP3",
    direction: "vertical",
    gap: "SP2",
    border: "1px solid #DFE5EB",
    borderRadius: "6px"
  }, reportContent, isPreviewVisible && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Divider, null), previewContent)), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Button, {
    size: "tiny",
    onClick: onClick
  }, "Open Report")));
});

/*
<<{
      "text": "Here’s a report for your top selling items in the last 14 days:",
      "widget": "REPORT_BUTTON",
      "data": {
          "reportId": "5f064c2c-ed38-4f9e-a985-3298ba8071a7",
          "query": {
            "id": "f109e7ab-2192-4251-b949-d39d7629fb83",
            "fields": ["traffic_main.page_url_from_val", "traffic_main.view_count"],
            "sort":{
              "fieldName": "traffic_main.view_count",
              "order": "DESC"
            },
            "paging": {
              "limit": 5,
              "offset": 0
            },
            "timeframe":{
              "startDate": "2024-01-24",
              "endDate": "2024-02-23"
            }
          }
      },
  }>>
*/
//# sourceMappingURL=ReportButton.js.map